import type {
  CustomChartDefinition,
  CustomChartTableSettings,
  CustomChartTableView,
  CustomChartType,
} from '@/entities/dashboards/model/api/types';

/** Типы диаграмм, вид колонок которых прежний клиент запоминал за смотрящим. */
const LEGACY_TABLE_VIEW_CHART_TYPES: readonly CustomChartType[] = ['table', 'grouped-table'];

/**
 * Свой вид колонок таблицы из личной настройки — поверх вида дашборда.
 *
 * Своим считается только вид, записанный правкой пользователя: такая запись идёт всегда целиком,
 * вместе с шириной колонок. Запись без ширины оставил прежний клиент — он писал состав колонок на
 * каждом открытии таблицы, и правкой пользователя она не была: прочитанная своим видом, она
 * заслонила бы вид дашборда у каждого, кто таблицу хоть раз видел.
 */
export const readOwnChartTableView = (
  settings: CustomChartTableSettings | undefined,
): CustomChartTableView | undefined => {
  if (!settings?.columnSizing) return undefined;
  const { visibleColumns, columnOrder, columnSizing } = settings;
  return { visibleColumns, columnOrder, columnSizing };
};

/**
 * Настройка колонок, которую прежний клиент запомнил за смотрящим, — у таблицы, вида колонок в
 * дашборде ещё не имеющей. Владельцу дашборда она переходит видом колонок дашборда.
 */
export const readLegacyChartTableView = (
  chart: CustomChartDefinition,
  settings: CustomChartTableSettings | undefined,
): CustomChartTableView | undefined => {
  if (chart.tableView || !LEGACY_TABLE_VIEW_CHART_TYPES.includes(chart.chartType)) return undefined;
  if (!settings || settings.columnSizing || !settings.visibleColumns?.length) return undefined;
  return { visibleColumns: settings.visibleColumns, columnOrder: settings.columnOrder };
};

/** Диаграмма с видом колонок `view`; без вида — таблица показана всеми колонками выборки. */
export const withChartTableView = (
  chart: CustomChartDefinition,
  view: CustomChartTableView | undefined,
): CustomChartDefinition => {
  if (view) return { ...chart, tableView: view };
  const { tableView: _dropped, ...rest } = chart;
  return rest;
};
